import React, { useEffect, useRef, useState } from 'react';
import { API_SERVER_URL } from 'js/constants/api';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import {
  Check as CopiedIcon,
  CloudDownload as DownloadIcon,
  FileCopy as CopyIcon,
  VpnKey as AccessIcon,
} from '@mui/icons-material';
import { ROUTE_SHOW_TOKEN } from 'js/constants/pathnames';
import Button from '../elements/editors/mui/Button';
import { useHistory } from 'react-router-dom';
import { CLI_DOWNLOAD_LINK } from 'js/constants/config';

/** Milliseconds per second (time base for UI delays). */
const MS_PER_SECOND = 1000;
/** How long the copy-success state stays visible before resetting. */
const COPY_FEEDBACK_VISIBLE_SECONDS = 5;

/**
 * Time until copy buttons return to their default label/icon after a successful copy.
 * Exported for tests — keep in sync with {@link COPY_FEEDBACK_VISIBLE_SECONDS}.
 */
export const COPY_FEEDBACK_RESET_MS = COPY_FEEDBACK_VISIBLE_SECONDS * MS_PER_SECOND;

interface TokenContentProps {
  showCLIDownload: boolean;
  user?: SessionTokenPanelUser;
}

interface SessionTokenPanelUser {
  full_name: string;
  token: string;
}

const styles = {
  overlayBody: {
    border: 'none',
  },
  copyButtonStyleDefault: {
    height: 'auto',
    alignSelf: 'center',
    margin: 0,
    border: 4,
    opacity: 1,
    minWidth: 'unset',
  },
  copyButtonStyleCopied: {
    height: 'auto',
    margin: 0,
    border: 4,
    opacity: 0.8,
    minWidth: 'unset',
  },
  buttonStyle: {
    width: 'auto',
    minWidth: 150,
    lineHeight: '38px',
    height: 'auto',
    marginRight: 0,
    marginLeft: 0,
    border: 4,
    padding: 0,
  },
};

const TokenCLI = () => {
  const history = useHistory();
  const _routeTokenView = () => history.push(ROUTE_SHOW_TOKEN);

  return (
    <div className="token-content">
      <h2 className="content-title">Nexla CLI</h2>
      <div
        className="content-subtitle"
        style={{
          textAlign: 'center',
          fontSize: 'var(--font-size-base-sm)',
          fontWeight: 'var(--font-weight-regular)',
          marginTop: 70,
          marginBottom: 'var(--spacing-lg)',
        }}
      >
        Get the latest CLI by clicking on the download button below. Check out{' '}
        <a
          href="https://docs.nexla.com/dev-guides/nexla-cli"
          target="_blank"
          rel="noopener noreferrer"
        >
          Nexla Developer Center
        </a>{' '}
        for instructions on using the CLI.
      </div>
      <div>
        <div>
          <div style={{ marginBottom: 12 }}>
            <Button
              color="primary"
              variant="contained"
              style={styles.copyButtonStyleDefault}
              download
              component="a"
              href={CLI_DOWNLOAD_LINK}
              startIcon={<DownloadIcon fontSize="large" htmlColor="#ffffff" />}
            >
              Download Nexla CLI
            </Button>
          </div>
          <div>
            <Button
              buttonTheme="dark"
              rootStyle={styles.copyButtonStyleDefault}
              startIcon={<AccessIcon fontSize="large" htmlColor="#ffffff" />}
              onClick={_routeTokenView}
            >
              View Session Token
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

type CopyKind = 'token' | 'url';

const clearTimeoutRef = (ref: React.MutableRefObject<ReturnType<typeof setTimeout> | null>) => {
  if (ref.current !== null) {
    clearTimeout(ref.current);
    ref.current = null;
  }
};

const SessionTokenPanel = (props: { user: SessionTokenPanelUser }) => {
  const [state, setState] = useState({
    tokenCopied: false,
    urlCopied: false,
  });
  const tokenCopiedResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const urlCopiedResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { user } = props;
  const { tokenCopied, urlCopied } = state;

  useEffect(() => {
    return () => {
      clearTimeoutRef(tokenCopiedResetTimeoutRef);
      clearTimeoutRef(urlCopiedResetTimeoutRef);
    };
  }, []);

  const handleCopySuccess = (copyKind: CopyKind) => {
    if (copyKind === 'token') {
      clearTimeoutRef(tokenCopiedResetTimeoutRef);
      clearTimeoutRef(urlCopiedResetTimeoutRef);
      setState({
        tokenCopied: true,
        urlCopied: false,
      });
      tokenCopiedResetTimeoutRef.current = setTimeout(() => {
        setState((prev) => ({ ...prev, tokenCopied: false }));
        tokenCopiedResetTimeoutRef.current = null;
      }, COPY_FEEDBACK_RESET_MS);
    } else {
      clearTimeoutRef(urlCopiedResetTimeoutRef);
      clearTimeoutRef(tokenCopiedResetTimeoutRef);
      setState({
        tokenCopied: false,
        urlCopied: true,
      });
      urlCopiedResetTimeoutRef.current = setTimeout(() => {
        setState((prev) => ({ ...prev, urlCopied: false }));
        urlCopiedResetTimeoutRef.current = null;
      }, COPY_FEEDBACK_RESET_MS);
    }
  };

  return (
    <div className="token-content">
      <h2 className="content-title">{`Hi, ${user.full_name}`}</h2>
      <div
        className="content-subtitle"
        style={{
          textAlign: 'center',
          fontSize: 'var(--font-size-base-sm)',
          fontWeight: 'var(--font-weight-regular)',
          marginBottom: 50,
        }}
      >
        Below are your Nexla API URL and Session Token. Copy the URL and token, and when prompted,
        paste the them into the Nexla CLI or Nexla API SDK.
      </div>
      <div className="url-container">
        <div className="copy-container-label">Nexla API URL</div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <pre style={{ flex: 1, marginRight: 12 }} className="url-snippet">
            {API_SERVER_URL}
          </pre>
          <div className="copy-url" style={{ justifySelf: 'flex-end' }}>
            <CopyToClipboard
              text={API_SERVER_URL}
              onCopy={(_text, result) => {
                if (result) {
                  handleCopySuccess('url');
                }
              }}
            >
              <Button
                buttonTheme="primary"
                startIcon={
                  urlCopied === true ? (
                    <CopiedIcon fontSize="large" htmlColor="#ffffff" />
                  ) : (
                    <CopyIcon fontSize="large" htmlColor="#ffffff" />
                  )
                }
                type={'primary'}
                rootStyle={
                  urlCopied === true ? styles.copyButtonStyleCopied : styles.copyButtonStyleDefault
                }
              ></Button>
            </CopyToClipboard>
          </div>
        </div>
      </div>
      <div style={{ flex: 1 }}>
        <div>
          <div className="copy-container-label">Session Token</div>
          <div>
            <pre className="token-snippet">
              ***** Token Masked. Use copy button{' '}
              <span role="img" aria-label="below">
                👇
              </span>
              *****
            </pre>
          </div>
          <div style={{ justifySelf: 'flex-end', marginTop: 'auto' }}>
            <CopyToClipboard
              text={user.token}
              onCopy={(_text, result) => {
                if (result) {
                  handleCopySuccess('token');
                }
              }}
            >
              <Button
                color="primary"
                variant="contained"
                buttonTheme="primary"
                startIcon={
                  tokenCopied === true ? (
                    <CopiedIcon fontSize="large" htmlColor="#ffffff" />
                  ) : (
                    <CopyIcon fontSize="large" htmlColor="#ffffff" />
                  )
                }
                style={
                  tokenCopied === true
                    ? styles.copyButtonStyleCopied
                    : styles.copyButtonStyleDefault
                }
                rootStyle={
                  tokenCopied === true
                    ? styles.copyButtonStyleCopied
                    : styles.copyButtonStyleDefault
                }
              >
                {tokenCopied === true ? `Copied Token to Clipboard` : 'Copy Token to Clipboard'}
              </Button>
            </CopyToClipboard>
          </div>
        </div>
      </div>
    </div>
  );
};

export const TokenContent = (props: TokenContentProps) => {
  const { showCLIDownload, user } = props;
  const contentView =
    showCLIDownload === true ? (
      <TokenCLI />
    ) : user != null ? (
      <SessionTokenPanel user={user} />
    ) : null;
  return <div className="token-container">{contentView}</div>;
};
